import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Camera, Users, ClipboardList, Send, Settings, LogOut, Zap } from "lucide-react";

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const items = [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/app/new-estimate", label: "New Estimate", icon: Camera, testid: "nav-new-estimate" },
    { to: "/app/estimates", label: "Estimates", icon: ClipboardList, testid: "nav-estimates" },
    { to: "/app/clients", label: "Clients", icon: Users, testid: "nav-clients" },
    { to: "/app/followups", label: "Follow-ups", icon: Send, testid: "nav-followups" },
    { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside className="hidden md:flex w-60 flex-col border-r border-border bg-card">
        <Link to="/app" className="flex items-center gap-2 px-6 py-6 border-b border-border">
          <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-sm">
            <Zap className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <span className="font-display text-2xl font-black tracking-tight">QUOTEBOT</span>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/app"}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted"
                }`
              }
            >
              <it.icon className="w-4 h-4" strokeWidth={2} />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Signed in</p>
            <p className="text-sm font-medium truncate" data-testid="signed-in-email">{user?.email}</p>
            <p className="text-xs text-muted-foreground">{user?.business_name || "Contractor"}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); nav("/"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm hover:bg-muted transition-colors text-foreground"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b border-border flex items-center justify-between px-4 py-3">
        <Link to="/app" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary flex items-center justify-center rounded-sm">
            <Zap className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-black tracking-tight">QUOTEBOT</span>
        </Link>
        <button data-testid="mobile-logout" onClick={() => { logout(); nav("/"); }} className="text-sm text-muted-foreground">
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      <main className="flex-1 min-h-screen pt-16 md:pt-0 pb-20 md:pb-0">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border grid grid-cols-5">
        {items.slice(0, 5).map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/app"}
            data-testid={`mobile-${it.testid}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2 text-[10px] ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <it.icon className="w-5 h-5 mb-1" />
            <span className="truncate max-w-[52px]">{it.label.split(" ")[0]}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
